import { ReactNode } from 'react';
import { LucideIcon, FileText, Search, Inbox, AlertCircle } from 'lucide-react';

type EmptyStateVariant = 'default' | 'search' | 'error' | 'minimal';

interface EmptyStateProps {
  /** Icon to display (lucide-react icon component) */
  icon?: LucideIcon;
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Primary action button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Secondary action (link style) */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Visual variant */
  variant?: EmptyStateVariant;
  /** Custom class name */
  className?: string;
  /** Children for custom content below description */
  children?: ReactNode;
}

const variantStyles: Record<EmptyStateVariant, { iconBg: string; iconColor: string }> = {
  default: { iconBg: 'bg-gray-100', iconColor: 'text-gray-400' },
  search: { iconBg: 'bg-gray-50', iconColor: 'text-gray-400' },
  error: { iconBg: 'bg-red-50', iconColor: 'text-red-400' },
  minimal: { iconBg: 'bg-gray-50', iconColor: 'text-gray-300' },
};

const defaultIcons: Record<EmptyStateVariant, LucideIcon> = {
  default: Inbox,
  search: Search,
  error: AlertCircle,
  minimal: FileText,
};

/**
 * Reusable empty state component following best practices:
 * - Clear visual hierarchy with icon, title, description
 * - Optional call-to-action button
 * - Multiple variants for different contexts
 * - Accessible with proper ARIA attributes
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'default',
  className = '',
  children,
}: EmptyStateProps) {
  const styles = variantStyles[variant];
  const Icon = icon || defaultIcons[variant];

  return (
    <div
      className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}
      role="status"
      aria-label={title}
    >
      {/* Icon container */}
      <div
        className={`w-16 h-16 rounded-full ${styles.iconBg} flex items-center justify-center mb-4`}
      >
        <Icon className={`w-8 h-8 ${styles.iconColor}`} aria-hidden="true" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>

      {/* Description */}
      {description && (
        <p className="mt-2 text-sm text-gray-500 max-w-md">{description}</p>
      )}

      {/* Custom children */}
      {children && <div className="mt-4">{children}</div>}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            >
              {action.icon && <action.icon className="w-4 h-4" aria-hidden="true" />}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="text-sm text-gray-700 hover:text-gray-900 font-medium"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empty state specifically for filtered/search results
 */
export function EmptySearchState({
  query,
  onClearFilters,
  entityName = 'results',
}: {
  query?: string;
  onClearFilters?: () => void;
  entityName?: string;
}) {
  return (
    <EmptyState
      variant="search"
      icon={Search}
      title={`No ${entityName} found`}
      description={
        query
          ? `No ${entityName} match "${query}". Try adjusting your search or filters.`
          : `No ${entityName} match your current filters.`
      }
      secondaryAction={
        onClearFilters
          ? { label: 'Clear filters', onClick: onClearFilters }
          : undefined
      }
    />
  );
}

/**
 * Empty state for when there's no data yet (first-time use)
 */
export function EmptyFirstTimeState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  ActionIcon,
}: {
  icon?: LucideIcon;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  ActionIcon?: LucideIcon;
}) {
  return (
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      action={{
        label: actionLabel,
        onClick: onAction,
        icon: ActionIcon,
      }}
    />
  );
}
